import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    getAuth,
    onAuthStateChanged,
    signOut,
    isSignInWithEmailLink,
    signInWithEmailLink,
    sendSignInLinkToEmail,
    updateProfile,
    sendPasswordResetEmail,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, DEFAULT_STORE_ID } from '../firebase/firebase';
import { migrateGuestCart } from '../firebase/cart-functions';

const UserContext = createContext({});

export const UserProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const auth = getAuth();

    // Initialize activeStoreId with DEFAULT_STORE_ID as the fallback
    const [activeStoreId, setActiveStoreId] = useState(
        localStorage.getItem('activeStoreId') || DEFAULT_STORE_ID
    );

    // Firebase actionCodeSettings for email link authentication
    const actionCodeSettings = {
        url: `${window.location.origin}/auth`,
        handleCodeInApp: true,
    };

    // Ensure DEFAULT_STORE_ID is always set in localStorage
    useEffect(() => {
        if (!localStorage.getItem('activeStoreId') && DEFAULT_STORE_ID) {
            localStorage.setItem('activeStoreId', DEFAULT_STORE_ID);
            setActiveStoreId(DEFAULT_STORE_ID);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            // console.log('UserContext - Auth state changed:', !!user);
            setCurrentUser(user);

            if (user) {
                try {
                    // Clear any previous errors when user authenticates
                    setError(null);

                    // First, try to migrate any guest cart
                    try {
                        await migrateGuestCart(user.uid);
                    } catch (cartErr) {
                        console.error("Error migrating guest cart:", cartErr);
                    }

                    // Then fetch user profile data
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (userDoc.exists()) {
                        setUserProfile(userDoc.data());

                        // Check if user has access to the active store
                        const userData = userDoc.data();
                        const userStoreId = userData.storeId;

                        // If user has a store but the current active store doesn't match,
                        // and their storeId matches DEFAULT_STORE_ID, switch to it
                        if (userStoreId &&
                            userStoreId !== activeStoreId &&
                            userStoreId === DEFAULT_STORE_ID) {
                            switchActiveStore(DEFAULT_STORE_ID);
                        }
                    } else {
                        // Create a basic profile if it doesn't exist (new user registration)
                        const basicProfile = {
                            email: user.email,
                            displayName: user.displayName,
                            phoneNumber: user.phoneNumber,
                            createdAt: serverTimestamp(),
                            role: 'customer',
                            storeId: DEFAULT_STORE_ID,
                            isNewUser: true
                        };

                        await setDoc(doc(db, 'users', user.uid), basicProfile);
                        setUserProfile(basicProfile);

                        // Also add the user to the store's customers collection
                        await addUserToStore(user, DEFAULT_STORE_ID);
                    }
                } catch (err) {
                    console.error('Error fetching user profile:', err);
                }
            } else {
                setUserProfile(null);

                // When logging out, ensure DEFAULT_STORE_ID is set
                if (localStorage.getItem('activeStoreId') !== DEFAULT_STORE_ID) {
                    localStorage.setItem('activeStoreId', DEFAULT_STORE_ID);
                    setActiveStoreId(DEFAULT_STORE_ID);
                }
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [activeStoreId]);

    // Helper function to add a user to a store's customers collection
    const addUserToStore = async (user, storeId) => {
        try {
            if (!user || !storeId) return;

            const storeCustomerRef = doc(db, "stores", storeId, "customers", user.uid);
            const storeCustomerDoc = await getDoc(storeCustomerRef);

            if (!storeCustomerDoc.exists()) {
                await setDoc(storeCustomerRef, {
                    email: user.email,
                    displayName: user.displayName || '',
                    phoneNumber: user.phoneNumber || '',
                    createdAt: serverTimestamp(),
                    orders: []
                });

                // Also update the user's main profile to include this store
                const userRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userRef);

                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    const currentStoreId = userData.storeId;

                    // Only update if user doesn't have a storeId set
                    if (!currentStoreId) {
                        await updateDoc(userRef, {
                            storeId: storeId
                        });
                    }
                }
            }
        } catch (error) {
            console.error("Error adding user to store:", error);
        }
    };

    // Function to switch active store
    const switchActiveStore = (storeId) => {
        if (!storeId) storeId = DEFAULT_STORE_ID;
        localStorage.setItem('activeStoreId', storeId);
        setActiveStoreId(storeId);
    };

    // Firebase built-in email link authentication with password support
    const sendAuthLinkToEmail = async (email, userData = {}, storeId = DEFAULT_STORE_ID) => {
        setError(null);
        try {
            const targetStoreId = storeId || DEFAULT_STORE_ID;
            const { password, isRegistering, ...additionalData } = userData;

            // If password is provided, use email/password authentication
            if (password) {
                try {
                    let authResult;

                    if (isRegistering) {
                        // Create new account with email/password
                        authResult = await createUserWithEmailAndPassword(auth, email, password);

                        // Update profile if displayName provided
                        if (additionalData.displayName) {
                            await updateProfile(authResult.user, {
                                displayName: additionalData.displayName
                            });
                        }

                        // Create user profile in Firestore
                        await setDoc(doc(db, 'users', authResult.user.uid), {
                            email: authResult.user.email,
                            displayName: additionalData.displayName || '',
                            phoneNumber: additionalData.phoneNumber || '',
                            firstName: additionalData.firstName || '',
                            lastName: additionalData.lastName || '',
                            createdAt: serverTimestamp(),
                            role: 'customer',
                            storeId: targetStoreId,
                            isNewUser: true,
                            lastSignIn: serverTimestamp(),
                            signInMethod: 'password'
                        });

                        await addUserToStore(authResult.user, targetStoreId);
                    } else {
                        // Sign in with existing account
                        authResult = await signInWithEmailAndPassword(auth, email, password);

                        // Update last sign-in
                        await updateDoc(doc(db, 'users', authResult.user.uid), {
                            lastSignIn: serverTimestamp(),
                            signInMethod: 'password'
                        });
                    }

                    localStorage.setItem('activeStoreId', targetStoreId);
                    setActiveStoreId(targetStoreId);

                    return {
                        success: true,
                        user: authResult.user,
                        method: 'password'
                    };

                } catch (passwordError) {
                    // If password auth fails and user doesn't exist, might want to suggest registration
                    if (passwordError.code === 'auth/user-not-found' && !isRegistering) {
                        throw new Error('No account found with this email. Please register first.');
                    } else if (passwordError.code === 'auth/wrong-password') {
                        throw new Error('Incorrect password. Please try again.');
                    } else if (passwordError.code === 'auth/email-already-in-use') {
                        throw new Error('An account with this email already exists. Please sign in.');
                    }
                    throw passwordError;
                }
            }

            // Use Firebase's built-in email link authentication (passwordless)
            localStorage.setItem('emailForSignIn', email);
            localStorage.setItem('pendingStoreId', targetStoreId);

            if (Object.keys(additionalData).length > 0) {
                localStorage.setItem('pendingUserData', JSON.stringify(additionalData));
            }

            // Send sign-in link using Firebase's native method
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);

            return {
                success: true,
                emailSent: true,
                message: 'Check your email for the sign-in link!'
            };

        } catch (err) {
            console.error('Auth error:', err);
            setError(err.message);
            throw new Error(err.message);
        }
    };

    //  Complete sign-in
    const completeEmailLinkSignIn = async (emailLink = window.location.href) => {
        setError(null);
        try {
            // console.log('Attempting to complete email link sign-in with URL:', emailLink);

            //  Just check if it's a valid Firebase email link
            if (!isSignInWithEmailLink(auth, emailLink)) {
                throw new Error('Invalid or expired sign-in link');
            }

            // Get the email from localStorage
            let email = localStorage.getItem('emailForSignIn');

            // If email is not available, prompt the user
            if (!email) {
                email = window.prompt('Please provide your email for confirmation');
                if (!email) {
                    throw new Error('Email is required to complete sign-in');
                }
            }

            // console.log('Completing sign-in for email:', email);

            // Sign in with the email link
            const result = await signInWithEmailLink(auth, email, emailLink);
            const user = result.user;

            // console.log('Email link sign-in successful for:', user.email);

            // Get stored data
            const pendingStoreId = localStorage.getItem('pendingStoreId') || DEFAULT_STORE_ID;
            const pendingUserDataStr = localStorage.getItem('pendingUserData');
            const pendingUserData = pendingUserDataStr ? JSON.parse(pendingUserDataStr) : {};

            // Check if this is a new user by seeing if they have a profile
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const isNewUser = !userDoc.exists();

            if (isNewUser && Object.keys(pendingUserData).length > 0) {
                // This is a new user registration - update their profile
                if (pendingUserData.displayName) {
                    await updateProfile(user, {
                        displayName: pendingUserData.displayName
                    });
                }

                // Create user profile with the stored data
                const userProfileData = {
                    email: user.email,
                    displayName: pendingUserData.displayName || '',
                    phoneNumber: pendingUserData.phoneNumber || '',
                    firstName: pendingUserData.firstName || '',
                    lastName: pendingUserData.lastName || '',
                    createdAt: serverTimestamp(),
                    role: 'customer',
                    storeId: pendingStoreId,
                    isNewUser: true,
                    lastSignIn: serverTimestamp(),
                    signInMethod: 'emailLink'
                };

                await setDoc(doc(db, 'users', user.uid), userProfileData);
            } else if (userDoc.exists()) {
                // Update last sign-in for existing users
                await updateDoc(doc(db, 'users', user.uid), {
                    lastSignIn: serverTimestamp(),
                    signInMethod: 'emailLink'
                });
            }

            // Add user to the store
            if (pendingStoreId) {
                await addUserToStore(user, pendingStoreId);
                localStorage.setItem('activeStoreId', pendingStoreId);
                setActiveStoreId(pendingStoreId);
            }

            // Clean up localStorage
            localStorage.removeItem('emailForSignIn');
            localStorage.removeItem('pendingStoreId');
            localStorage.removeItem('pendingUserData');

            return { user, isNewUser };
        } catch (err) {
            console.error('Error completing email link sign-in:', err);
            setError(err.message);
            throw new Error(err.message);
        }
    };

    //  Check for email link
    const checkForEmailLink = (url = window.location.href) => {
        // console.log('Checking if URL is email link:', url);

        try {
            const isEmailLink = isSignInWithEmailLink(auth, url);
            // console.log('Is email link:', isEmailLink);
            return isEmailLink;
        } catch (error) {
            console.error('Error checking email link:', error);
            return false;
        }
    };

    // Legacy function names for compatibility
    const loginWithEmail = async (email, password, storeId = DEFAULT_STORE_ID) => {
        // console.warn('loginWithEmail is deprecated, using email link authentication instead');
        return await sendAuthLinkToEmail(email, {}, storeId);
    };

    const register = async (email, password, userData = {}, storeId = DEFAULT_STORE_ID) => {
        // console.warn('register is deprecated, using email link authentication instead');
        return await sendAuthLinkToEmail(email, userData, storeId);
    };

    //logout with complete cleanup
    const logout = async () => {
        setError(null);
        try {
            await signOut(auth);

            // Enhanced cleanup
            localStorage.setItem('activeStoreId', DEFAULT_STORE_ID);
            setActiveStoreId(DEFAULT_STORE_ID);

            // Clear all auth-related data
            localStorage.removeItem('emailForSignIn');
            localStorage.removeItem('pendingStoreId');
            localStorage.removeItem('pendingUserData');

        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Forgot Password functionality using Firebase default
    const sendPasswordReset = async (email) => {
        setError(null);
        try {
            // Use Firebase's built-in password reset email
            await sendPasswordResetEmail(auth, email);
            return {
                success: true,
                message: 'Password reset email sent successfully!'
            };
        } catch (err) {
            console.error('Password reset error:', err);
            let errorMessage = 'Failed to send password reset email. Please try again.';

            // Handle specific Firebase auth errors
            switch (err.code) {
                case 'auth/user-not-found':
                    errorMessage = 'No account found with this email address.';
                    break;
                case 'auth/invalid-email':
                    errorMessage = 'Please enter a valid email address.';
                    break;
                case 'auth/too-many-requests':
                    errorMessage = 'Too many requests. Please try again later.';
                    break;
                default:
                    errorMessage = err.message || errorMessage;
            }

            setError(errorMessage);
            throw new Error(errorMessage);
        }
    };

    const value = {
        currentUser,
        userProfile,
        loading,
        error,
        activeStoreId,

        // Error management
        setError,

        // Simplified email link methods
        sendAuthLinkToEmail,
        completeEmailLinkSignIn,
        checkForEmailLink,

        // Legacy methods
        loginWithEmail,
        register,

        // Password reset
        sendPasswordReset,

        logout,
        switchActiveStore,
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};